import moment from 'moment'

// Folio action rules shared by the booking screens: shorten stay, cancel folio / invoice, payments.

// Returns the reason the stay cannot be shortened this much, or null when it is allowed.
// uom is 'Day' or 'Hour'; status is the allocation status ('Blocked', 'Occupied', ...).
export function shortenBlockReason(start, end, qty, uom, status) {
    let n = parseInt(qty);
    if (isNaN(n) || n <= 0)
    {
        return `Invalid: Please choose how much to shorten the stay.`;
    }

    if (uom === 'Hour')
    {
        let newEnd = moment(end).local().subtract(n, 'h');
        if (!newEnd.isAfter(moment(start).local()))
        {
            return `Invalid: Shorten Stay NOT ALLOWED. The new departure would be on or before arrival.`;
        }
        if (status === 'Occupied' && newEnd.isBefore(moment()))
        {
            return `Invalid: Shorten Stay NOT ALLOWED. The new departure would be in the past.`;
        }
        return null;
    }

    let newEnd = moment(end).local().subtract(n, 'd').startOf('day');
    if (!newEnd.isAfter(moment(start).local().startOf('day')))
    {
        return `Invalid: Shorten Stay NOT ALLOWED. The new departure (${newEnd.format('DD-MM-YYYY')}) would be on or before arrival (${moment(start).local().format('DD-MM-YYYY')}).`;
    }
    if (status === 'Occupied' && newEnd.isBefore(moment().startOf('day')))
    {
        return `Invalid: Shorten Stay NOT ALLOWED. The new departure (${newEnd.format('DD-MM-YYYY')}) would be before today.`;
    }
    return null;
}

// Returns the reason this user may not cancel a folio or invoice, or null when allowed.
// Administrator, manager and supervisor may cancel; cashier only when allowUserCancelFolio is on.
export function cancelBlockReason(role, customSettings, what = 'folio') {
    if (role === 'administrator' || role === 'manager' || role === 'supervisor')
    {
        return null;
    }

    if (role === 'cashier' && customSettings && customSettings.data && customSettings.data.allowUserCancelFolio === true)
    {
        return null;
    }

    if (role === 'cashier')
    {
        return `Invalid: Cashier is not authorized to cancel ${what}. Please contact the supervisor or administrator.`;
    }

    return `Invalid: Your role is not authorized to cancel ${what}. Please contact the supervisor or administrator.`;
}

// Net amount paid on a folio: receipts minus refunds (remittances).
export function netPaid(payments) {
    let paid = 0;
    let refund = 0;
    for (let i of payments || [])
    {
        if (i.type === 'receipt')
        {
            paid = paid + parseFloat(i.amount);
        }
        else
        {
            refund = refund + parseFloat(i.amount);
        }
    }
    return paid - refund;
}

// Same as Windows frmEditFolios/frmCheckInOut btnPayment_Click: a walk-in folio with no OTA/agency
// payor may only refund up to the overpaid amount, unless "Allow Any Walk-In Refund" is on.
export function walkinRefundLimited(folio, customSettings) {
    if (folio === undefined || folio === null || folio.source !== 'walkin')
    {
        return false;
    }

    if (customSettings && customSettings.data && customSettings.data.walkinAnyRefund === true)
    {
        return false;
    }

    let payor = folio.data && folio.data.payor ? folio.data.payor : null;
    let agency = payor ? payor.otaagency : null;

    return agency === undefined || agency === null || agency.id === undefined || agency.id === null || agency.id === '';
}

function customSettingsNow() {
    try
    {
        return JSON.parse(localStorage.getItem('customsetting'));
    }
    catch (err)
    {
        return null;
    }
}

// Returns the reason the payment or refund cannot be made, or null when it is allowed.
// type is 'Payment' or 'Refund'; paymentFor is 'Deposit' or 'Payment'. Deposits are not capped.
// folio (optional) turns on the Windows walk-in refund limit.
export function paymentBlockReason(type, paymentFor, amount, folioAmount, payments, folio) {
    let amt = parseFloat(amount);
    if (isNaN(amt) || amt <= 0)
    {
        return `Invalid: Please enter an Amount greater than 0.`;
    }

    // Round to cents so floating point sums don't block an exact payment.
    let net = Math.round(netPaid(payments) * 100) / 100;
    let balance = Math.round((parseFloat(folioAmount) - net) * 100) / 100;
    amt = Math.round(amt * 100) / 100;

    if (type === 'Refund')
    {
        if (amt > net)
        {
            return `Invalid: Refund NOT ALLOWED. The refund (${amt.toFixed(2)}) is more than the net amount paid (${net.toFixed(2)}).`;
        }

        if (walkinRefundLimited(folio, customSettingsNow()))
        {
            let toRefund = balance < 0 ? balance * -1 : 0;
            if (amt > toRefund)
            {
                return `Invalid: Refund Amount is greater than Balance to Refund! Walk-in folios may only refund the overpaid amount (${toRefund.toFixed(2)}).`;
            }
        }
        return null;
    }

    if (paymentFor !== 'Deposit' && amt > balance)
    {
        return `Invalid: Payment NOT ALLOWED. The amount (${amt.toFixed(2)}) is more than the balance (${(balance > 0 ? balance : 0).toFixed(2)}). Use Deposit for an advance or security deposit.`;
    }
    return null;
}

// Same as Windows blockDiscountCashier: a cashier may not enter Open Price or Discount when the setting is on.
export function discountAllowed(role, customSettings) {
    if (role === 'cashier' && customSettings && customSettings.data && customSettings.data.blockDiscountCashier === true)
    {
        return false;
    }
    return true;
}

// Login saves the role as a plain string (cashier), not JSON.
function storedRole() {
    let r = localStorage.getItem('role');
    try
    {
        return JSON.parse(r);
    }
    catch (err)
    {
        return r;
    }
}

// Reads the logged-in role and the property's custom settings saved at login.
export function discountAllowedNow() {
    try
    {
        return discountAllowed(storedRole(), JSON.parse(localStorage.getItem('customsetting')));
    }
    catch (err)
    {
        return true;
    }
}

// Same as Windows fullPaymentBeforeInvoice: no invoice while the folio still has a balance.
// Returns the reason Create Invoice is blocked, or null when it is allowed.
export function invoiceBlockReason(customSettings, balance) {
    if (customSettings && customSettings.data && customSettings.data.fullPaymentBeforeInvoice === true)
    {
        let b = Math.round(parseFloat(balance) * 100) / 100;
        if (!isNaN(b) && b > 0)
        {
            return `Invalid: Full payment is required before issuing an invoice. Balance: ${b.toFixed(2)}.`;
        }
    }
    return null;
}

export function invoiceBlockReasonNow(balance) {
    try
    {
        return invoiceBlockReason(JSON.parse(localStorage.getItem('customsetting')), balance);
    }
    catch (err)
    {
        return null;
    }
}
